/**
 * AdminAuditReconciliation — operator panel for live GP audit + manual reconciliation.
 *
 * Backed by:
 *   GET  /api/audit/gp-reconciliation?date_from&date_to
 *   POST /api/audit/gp-reconciliation/compare  (multipart + manual.xlsx)
 *   GET  /api/audit/gp-reconciliation/export?date_from&date_to&upload_id
 *
 * Use cases:
 *  - Quick monthly view of Fuel GP / DEF GP / Combined / CAD-eq totals
 *  - Per-customer + per-network breakdown
 *  - Upload the operator's "Client wise margins" Excel → side-by-side
 *    variance table with rows flagged when |variance| > $10.
 *  - Download a single Excel with Summary / By Customer / By Network /
 *    Variance sheets for offline review.
 */
import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, FileXls, Upload, Warning } from "@phosphor-icons/react";

function fmt(n) {
  if (n === undefined || n === null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminAuditReconciliation() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState("2026-05-04");
  const [dateTo, setDateTo] = useState("2026-05-31");
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadSnapshot = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please select a date range first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/audit/gp-reconciliation", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setSnapshot(data);
      setComparison(null);
      setUploadId(null);
      toast.success(`Loaded ${data.row_count} transactions for ${dateFrom} → ${dateTo}`);
    } catch (e) {
      toast.error("Failed to load snapshot: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("date_from", dateFrom);
      fd.append("date_to", dateTo);
      fd.append("variance_threshold", "10");
      const { data } = await api.post("/audit/gp-reconciliation/compare", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setComparison(data);
      setUploadId(data.upload_id);
      toast.success(`Compared ${data.comparison.length} rows. Flagged: ${data.flagged_count}`);
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    }
    setUploading(false);
    // Reset file input so same file can be re-uploaded.
    e.target.value = "";
  };

  const exportExcel = async () => {
    const url = `/audit/gp-reconciliation/export?date_from=${dateFrom}&date_to=${dateTo}${
      uploadId ? `&upload_id=${uploadId}` : ""
    }`;
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `gp_reconciliation_${dateFrom}_to_${dateTo}.xlsx`;
      link.click();
    } catch (e) {
      toast.error("Export failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const totals = snapshot?.totals;

  return (
    <div className="space-y-6" data-testid="audit-reconciliation-page">
      <PageHeader
        title="GP Audit & Reconciliation"
        subtitle="Live Fuel / DEF / Combined GP with CAD-equivalent + side-by-side manual reconciliation."
      />

      {/* Date range + actions */}
      <div className="flex flex-wrap items-end gap-3 rounded border bg-white p-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500">From</label>
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded border px-3 py-2 font-mono text-sm"
            data-testid="recon-date-from"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500">To</label>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded border px-3 py-2 font-mono text-sm"
            data-testid="recon-date-to"
          />
        </div>
        <button
          onClick={loadSnapshot} disabled={loading}
          className="rounded bg-black px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          data-testid="recon-load-btn"
        >
          {loading ? <ArrowsClockwise className="inline animate-spin" /> : "Load snapshot"}
        </button>
        <label
          className="cursor-pointer rounded border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          data-testid="recon-upload-label"
        >
          <Upload className="-mt-0.5 mr-1 inline" />
          {uploading ? "Uploading…" : "Upload manual file"}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUpload} />
        </label>
        {snapshot && (
          <button
            onClick={exportExcel}
            className="rounded border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            data-testid="recon-export-btn"
          >
            <FileXls className="-mt-0.5 mr-1 inline" /> Export to Excel
          </button>
        )}
      </div>

      {/* Totals tiles */}
      {totals && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="recon-totals">
          <Tile label="Fuel GP" value={fmt(totals.fuel_gp)} sub={`CAD eq.: ${fmt(totals.fuel_gp_cad)}`} primary />
          <Tile label="DEF GP" value={fmt(totals.def_gp)} sub={`CAD eq.: ${fmt(totals.def_gp_cad)}`} />
          <Tile label="Combined GP" value={fmt(totals.combined_gp)} sub={`CAD eq.: ${fmt(totals.combined_gp_cad)}`} />
          <Tile label="Rows" value={(snapshot?.row_count || 0).toLocaleString()} sub={`${dateFrom} → ${dateTo}`} />
        </div>
      )}

      {/* By Network */}
      {snapshot?.by_network && (
        <Section title="By Network">
          <table className="w-full text-sm" data-testid="recon-by-network">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr><th className="p-2 text-left">Network</th><th className="p-2 text-right">Rows</th><th className="p-2 text-right">Fuel GP</th><th className="p-2 text-right">DEF GP</th><th className="p-2 text-right">Combined</th><th className="p-2 text-right">Combined (CAD eq.)</th></tr>
            </thead>
            <tbody>
              {snapshot.by_network.map((n) => (
                <tr key={n.network} className="border-t">
                  <td className="p-2 font-medium">{n.network}</td>
                  <td className="p-2 text-right font-mono">{n.rows}</td>
                  <td className="p-2 text-right font-mono">{fmt(n.fuel_gp)}</td>
                  <td className="p-2 text-right font-mono">{fmt(n.def_gp)}</td>
                  <td className="p-2 text-right font-mono">{fmt(n.combined_gp)}</td>
                  <td className="p-2 text-right font-mono font-semibold">{fmt(n.combined_gp_cad)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* By Customer */}
      {snapshot?.by_customer && (
        <Section title={`By Customer (${snapshot.by_customer.length})`}>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm" data-testid="recon-by-customer">
              <thead className="sticky top-0 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr><th className="p-2 text-left">Customer</th><th className="p-2 text-right">Rows</th><th className="p-2 text-right">Fuel GP</th><th className="p-2 text-right">DEF GP</th><th className="p-2 text-right">Combined</th><th className="p-2 text-right">CAD eq.</th></tr>
              </thead>
              <tbody>
                {snapshot.by_customer.map((c) => (
                  <tr key={c.customer_id} className="border-t hover:bg-zinc-50">
                    <td className="p-2">{c.customer_name}</td>
                    <td className="p-2 text-right font-mono">{c.rows}</td>
                    <td className="p-2 text-right font-mono">{fmt(c.fuel_gp)}</td>
                    <td className="p-2 text-right font-mono">{fmt(c.def_gp)}</td>
                    <td className="p-2 text-right font-mono">{fmt(c.combined_gp)}</td>
                    <td className="p-2 text-right font-mono font-semibold">{fmt(c.combined_gp_cad)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Variance table */}
      {comparison && (
        <Section title="Variance vs Manual File" rightLabel={`Flagged: ${comparison.flagged_count} (|Δ| > $${comparison.variance_threshold})`}>
          <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
            <div className="rounded bg-zinc-50 p-3"><div className="text-xs uppercase text-zinc-500">Manual total</div><div className="font-mono">{fmt(comparison.manual_total)}</div></div>
            <div className="rounded bg-zinc-50 p-3"><div className="text-xs uppercase text-zinc-500">System Fuel (CAD eq.)</div><div className="font-mono">{fmt(comparison.system_fuel_total_cad)}</div></div>
            <div className={`rounded p-3 ${Math.abs(comparison.variance_total) > 10 ? "bg-amber-50" : "bg-emerald-50"}`}>
              <div className="text-xs uppercase text-zinc-500">Total variance</div>
              <div className="font-mono">{fmt(comparison.variance_total)}</div>
            </div>
          </div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm" data-testid="recon-variance-table">
              <thead className="sticky top-0 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr><th className="p-2 text-left">Manual Customer</th><th className="p-2 text-left">System Customer</th><th className="p-2 text-right">Manual GP</th><th className="p-2 text-right">System GP (CAD)</th><th className="p-2 text-right">Variance</th><th className="p-2"></th></tr>
              </thead>
              <tbody>
                {comparison.comparison.map((r, i) => (
                  <tr key={i} className={`border-t ${r.flagged ? "bg-amber-50" : ""}`}>
                    <td className="p-2">{r.manual_customer}</td>
                    <td className="p-2 text-zinc-600">{r.system_customer}</td>
                    <td className="p-2 text-right font-mono">{fmt(r.manual_gp)}</td>
                    <td className="p-2 text-right font-mono">{fmt(r.system_gp_cad)}</td>
                    <td className={`p-2 text-right font-mono font-semibold ${r.flagged ? "text-amber-700" : "text-zinc-700"}`}>{fmt(r.variance)}</td>
                    <td className="p-2">{r.flagged && <Badge variant="warning"><Warning className="-mt-0.5 mr-1 inline" />Flag</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {!snapshot && !loading && (
        <div className="rounded border border-dashed bg-zinc-50 p-12 text-center text-sm text-zinc-500">
          Select a date range and click <b>Load snapshot</b> to view live GP totals.
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, primary }) {
  return (
    <div className={`rounded border bg-white p-4 ${primary ? "border-emerald-300" : ""}`} data-testid={`recon-tile-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-2xl ${primary ? "text-emerald-700" : "text-zinc-900"}`}>{value}</div>
      {sub && <div className="mt-1 font-mono text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

function Section({ title, rightLabel, children }) {
  return (
    <div className="rounded border bg-white">
      <div className="flex items-center justify-between border-b bg-zinc-50 px-4 py-2">
        <div className="text-sm font-semibold text-zinc-700">{title}</div>
        {rightLabel && <div className="text-xs text-zinc-500">{rightLabel}</div>}
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}
